import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfPen,
  PdfColor,
  PdfPoint,
  PdfRect,
  PdfFont,
  PdfBrush,
  ETextAlign,
  PageGen,
} from '../../pdf/pdf';
import { ILayoutBaseGen } from '../layoutBase';
import { OptsBase } from '../optsBase';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/cut';
import { GUtil } from '../../pdf/gutil';
import {
  GeoBoard,
  GeoBoardShape,
  EGeoBoardShapeType,
  getDefaultShapeOptions,
  shapesIntersect,
  randomGeoBoardShape,
} from '../../pdf/board/geoboard';
import { C } from '../../core/c';
import { GenData, Random } from '@educraft/base';

/**
 * Base class for GeoBoard transformation exercises
 * Draws shapes on a geoboard and shows their transformed versions
 */
abstract class GeoBoardTransformationBase implements ILayoutBaseGen {
  constructor(protected readonly board: GeoBoard) {}

  protected abstract transformation(board: GeoBoard, points: C[]): C[][];
  protected abstract description(data: GenData): string;
  protected get count(): number {
    return 2;
  }
  protected shapeSupported(board: GeoBoard, shape: GeoBoardShape): boolean {
    return true;
  }

  public async generate(pdf: PdfGen, opts: OptsBase): Promise<void> {
    const page = pdf.addPage();
    const random = opts.data?.random ?? new Random();

    const hintText = this.description(opts.data);
    page.drawString(
      hintText,
      GUtil.baseFont,
      PdfBrush.Black,
      null,
      new PdfRect(PdfUnit.FromMillimeters(5), PdfUnit.FromMillimeters(5), 50, 10),
      ETextAlign.TopLeft
    );

    // Draw board
    this.board.draw(page);
    this.board.drawMini(page);
    this.drawSpecific(page);

    // Generate non-overlapping shapes
    let counter = 0;
    let shapes: GeoBoardShape[] = [];
    const shapeOptions = getDefaultShapeOptions();

    while (true) {
      shapes = [];
      for (let i = 0; i < this.count; i++) {
        const shape = randomGeoBoardShape(shapeOptions, this.board.width, this.board.height, () =>
          random.nextDouble()
        );

        const ok =
          shapes.length === 0 || shapes.every((p) => !shapesIntersect(p.points, shape.points));

        if (this.shapeSupported(this.board, shape) && ok) {
          shapes.push(shape);
        }
      }

      if (shapes.length === this.count) break;
      if (counter++ > 10000) throw new Error('Timeout generating shapes');
    }

    const colors = GUtil.genMap(opts.data.random, GUtil.geoBoardColors.length);

    // Draw shapes
    for (let index = 0; index < shapes.length; index++) {
      const shape = shapes[index];
      const color = GUtil.geoBoardColors[colors[index]];
      const pen = new PdfPen(color, 2);
      const brush = new PdfBrush(PdfColor.fromColor(color, 0.08));

      // Draw on main board
      const mainPoints = shape.points.map((p) => {
        const pp = this.board.pointFromC(p);
        return new PdfPoint(
          pp.x + index * PdfUnit.FromMillimeters(0.2),
          pp.y + index * PdfUnit.FromMillimeters(0.2)
        );
      });
      page.drawPolygon(pen, brush, mainPoints);

      // Draw hint for first shape
      if (index === 0) {
        this.drawHint(page, shape);
      }

      // Draw on mini board - original
      const miniPoints1 = shape.points.map((p) => {
        const pp = this.board.pointMiniFromC(p);
        return new PdfPoint(
          pp.x + index * PdfUnit.FromMillimeters(0.2),
          pp.y + index * PdfUnit.FromMillimeters(0.2)
        );
      });
      page.drawPolygon(GUtil.basePen, null, miniPoints1);

      // Draw on mini board - transformed
      const transformedPoints = this.transformation(this.board, shape.points);
      for (const pts1 of transformedPoints) {
        const pts = pts1.map((p) => {
          const pp = this.board.pointMiniFromC(p);
          return new PdfPoint(
            pp.x + index * PdfUnit.FromMillimeters(0.2),
            pp.y + index * PdfUnit.FromMillimeters(0.2)
          );
        });
        page.drawPolygon(pen, brush, pts);
      }
    }
  }

  protected drawHint(page: PageGen, shape: GeoBoardShape): void {
    const point = shape.points.reduce((a, b) => (a.x < b.x ? a : b));
    const transformed = this.transformation(this.board, [point]);
    const dashPen = new PdfPen(PdfColor.fromRgb(0, 0, 0), 1);
    dashPen.dashArray = [2, 4];

    this.board.drawPointFromC(page, point, GUtil.basePen, null, PdfUnit.FromMillimeters(8));
    for (const pts of transformed) {
      const pp = pts[0];
      this.board.drawPointFromC(page, pp, GUtil.basePen, null, PdfUnit.FromMillimeters(8));
      this.board.drawPointFromC(page, point, null, PdfBrush.Black, PdfUnit.FromMillimeters(4));
      this.board.drawPointFromC(page, pp, null, PdfBrush.Black, PdfUnit.FromMillimeters(4));
      this.drawHintLine(page, dashPen, point, pp);
    }
  }

  protected drawHintLine(page: PageGen, pen: PdfPen, p1: C, p2: C): void {
    page.drawLine(pen, this.board.pointFromC(p1), this.board.pointFromC(p2));
  }

  protected drawSpecific(page: PageGen): void {}
}

// ===== Rotation 90 degrees clockwise =====

class GeoBoard_Rotace90CW_Base extends GeoBoardTransformationBase {
  protected get count(): number {
    return 1;
  }

  protected description(data: GenData): string {
    return data.lang.GeoBoard_Rotace90CW ?? 'Rotace 90° (po směru ručiček)';
  }

  protected transformation(board: GeoBoard, points: C[]): C[][] {
    return [points.map((p) => new C(board.width - 1 - p.y, p.x))];
  }

  protected shapeSupported(board: GeoBoard, shape: GeoBoardShape): boolean {
    return shape.points.every((p) => p.x <= board.width / 2 && p.y <= board.height / 2);
  }

  protected drawSpecific(page: PageGen): void {
    const cx = (this.board.width - 1) / 2;
    const cy = (this.board.height - 1) / 2;
    this.board.drawPoint(page, cx, cy, GUtil.basePen, null, PdfUnit.FromMillimeters(8));
    this.board.drawPoint(page, cx, cy, null, PdfBrush.Black, PdfUnit.FromMillimeters(4));
  }

  protected drawHintLine(page: PageGen, pen: PdfPen, p1: C, p2: C): void {
    const cx = (this.board.width - 1) / 2;
    const cy = (this.board.height - 1) / 2;
    const centerPt = this.board.point(cx, cy);
    const p1Pt = this.board.pointFromC(p1);
    const p2Pt = this.board.pointFromC(p2);

    page.drawArcBetweenPoints(pen, centerPt, p1Pt, p2Pt, true);

    const startAngle = (Math.atan2(p1Pt.y - centerPt.y, p1Pt.x - centerPt.x) * 180) / Math.PI;
    const endAngle = startAngle + 90;
    const arrowSize = PdfUnit.FromMillimeters(3);
    const arrowAngleRad = (endAngle * Math.PI) / 180;
    const tangentAngleRad = arrowAngleRad + Math.PI / 2; // For CW rotation, tangent points in direction of motion

    const arrow1 = {
      x: p2Pt.x + arrowSize * Math.cos(tangentAngleRad + Math.PI + Math.PI / 6),
      y: p2Pt.y + arrowSize * Math.sin(tangentAngleRad + Math.PI + Math.PI / 6),
    };
    const arrow2 = {
      x: p2Pt.x + arrowSize * Math.cos(tangentAngleRad + Math.PI - Math.PI / 6),
      y: p2Pt.y + arrowSize * Math.sin(tangentAngleRad + Math.PI - Math.PI / 6),
    };
    page.drawLine(pen, p2Pt, new PdfPoint(arrow1.x, arrow1.y));
    page.drawLine(pen, p2Pt, new PdfPoint(arrow2.x, arrow2.y));
  }
}

export class GeoBoard__8__Rotace90CW extends GeoBoard_Rotace90CW_Base {
  constructor() {
    super(GeoBoard.Board8());
  }
}

export class GeoBoard__920__Rotace90CW extends GeoBoard_Rotace90CW_Base {
  constructor() {
    super(GeoBoard.Board920());
  }
}

// ===== Rotation 90 degrees counter-clockwise =====

class GeoBoard_Rotace90CCW_Base extends GeoBoardTransformationBase {
  protected get count(): number {
    return 1;
  }

  protected description(data: GenData): string {
    return data.lang.GeoBoard_Rotace90CCW ?? 'Rotace 90° (proti směru ručiček)';
  }

  protected transformation(board: GeoBoard, points: C[]): C[][] {
    return [points.map((p) => new C(p.y, board.width - 1 - p.x))];
  }

  protected shapeSupported(board: GeoBoard, shape: GeoBoardShape): boolean {
    return shape.points.every((p) => p.x <= board.width / 2 && p.y <= board.height / 2);
  }

  protected drawSpecific(page: PageGen): void {
    const cx = (this.board.width - 1) / 2;
    const cy = (this.board.height - 1) / 2;
    this.board.drawPoint(page, cx, cy, GUtil.basePen, null, PdfUnit.FromMillimeters(8));
    this.board.drawPoint(page, cx, cy, null, PdfBrush.Black, PdfUnit.FromMillimeters(4));
  }

  protected drawHintLine(page: PageGen, pen: PdfPen, p1: C, p2: C): void {
    const cx = (this.board.width - 1) / 2;
    const cy = (this.board.height - 1) / 2;
    const centerPt = this.board.point(cx, cy);
    const p1Pt = this.board.pointFromC(p1);
    const p2Pt = this.board.pointFromC(p2);

    page.drawArcBetweenPoints(pen, centerPt, p1Pt, p2Pt, false);

    const startAngle = (Math.atan2(p1Pt.y - centerPt.y, p1Pt.x - centerPt.x) * 180) / Math.PI;
    const endAngle = startAngle - 90;
    const arrowSize = PdfUnit.FromMillimeters(3);
    const arrowAngleRad = (endAngle * Math.PI) / 180;
    const tangentAngleRad = arrowAngleRad - Math.PI / 2; // For CCW rotation, tangent points in direction of motion

    const arrow1 = {
      x: p2Pt.x + arrowSize * Math.cos(tangentAngleRad + Math.PI + Math.PI / 6),
      y: p2Pt.y + arrowSize * Math.sin(tangentAngleRad + Math.PI + Math.PI / 6),
    };
    const arrow2 = {
      x: p2Pt.x + arrowSize * Math.cos(tangentAngleRad + Math.PI - Math.PI / 6),
      y: p2Pt.y + arrowSize * Math.sin(tangentAngleRad + Math.PI - Math.PI / 6),
    };
    page.drawLine(pen, p2Pt, new PdfPoint(arrow1.x, arrow1.y));
    page.drawLine(pen, p2Pt, new PdfPoint(arrow2.x, arrow2.y));
  }
}

export class GeoBoard__8__Rotace90CCW extends GeoBoard_Rotace90CCW_Base {
  constructor() {
    super(GeoBoard.Board8());
  }
}

export class GeoBoard__920__Rotace90CCW extends GeoBoard_Rotace90CCW_Base {
  constructor() {
    super(GeoBoard.Board920());
  }
}

// ===== Horizontal symmetry (vertical axis) =====

class GeoBoard_Soumernost_Osa1V_Base extends GeoBoardTransformationBase {
  protected description(data: GenData): string {
    return data.lang.GeoBoard_Soumernost_Osa1V ?? 'Osová souměrnost (vertikálně)';
  }

  protected transformation(board: GeoBoard, points: C[]): C[][] {
    const centerX = (board.width - 1) / 2;

    const mirrored = points.map((p) => {
      return new C(Math.round(2 * centerX - p.x), p.y);
    });

    return [mirrored];
  }

  protected drawSpecific(page: PageGen): void {
    const centerX = (this.board.width - 1) / 2;
    const p1 = this.board.point(centerX, 0);
    const p2 = this.board.point(centerX, this.board.height - 1);
    const axisPen = new PdfPen(PdfColor.fromRgb(128, 128, 128), 1);
    axisPen.dashArray = [5, 3];
    page.drawLine(axisPen, p1, p2);
  }

  protected shapeSupported(board: GeoBoard, shape: GeoBoardShape): boolean {
    const centerX = (board.width - 1) / 2;
    return shape.points.every((p) => p.x < centerX - 0.5);
  }
}

export class GeoBoard__8__Soumernost_Osa1V extends GeoBoard_Soumernost_Osa1V_Base {
  constructor() {
    super(GeoBoard.Board8());
  }
}

export class GeoBoard__920__Soumernost_Osa1V extends GeoBoard_Soumernost_Osa1V_Base {
  constructor() {
    super(GeoBoard.Board920());
  }
}

// ===== Vertical symmetry (horizontal axis) =====

class GeoBoard_Soumernost_Osa1H_Base extends GeoBoardTransformationBase {
  protected description(data: GenData): string {
    return data.lang.GeoBoard_Soumernost_Osa1H ?? 'Osová souměrnost (horizontálně)';
  }

  protected transformation(board: GeoBoard, points: C[]): C[][] {
    const centerY = (board.height - 1) / 2;

    const mirrored = points.map((p) => {
      return new C(p.x, Math.round(2 * centerY - p.y));
    });

    return [mirrored];
  }

  protected drawSpecific(page: PageGen): void {
    const centerY = (this.board.height - 1) / 2;
    const p1 = this.board.point(0, centerY);
    const p2 = this.board.point(this.board.width - 1, centerY);
    const axisPen = new PdfPen(PdfColor.fromRgb(128, 128, 128), 1);
    axisPen.dashArray = [5, 3];
    page.drawLine(axisPen, p1, p2);
  }

  protected shapeSupported(board: GeoBoard, shape: GeoBoardShape): boolean {
    const centerY = (board.height - 1) / 2;
    return shape.points.every((p) => p.y < centerY - 0.5);
  }
}

export class GeoBoard__8__Soumernost_Osa1H extends GeoBoard_Soumernost_Osa1H_Base {
  constructor() {
    super(GeoBoard.Board8());
  }
}

export class GeoBoard__920__Soumernost_Osa1H extends GeoBoard_Soumernost_Osa1H_Base {
  constructor() {
    super(GeoBoard.Board920());
  }
}

// ===== Two-axis symmetry =====

class GeoBoard_Soumernost_Osy2_Base extends GeoBoardTransformationBase {
  protected description(data: GenData): string {
    return (
      data.lang.GeoBoard_Soumernost_Osy2 ?? 'Osová souměrnost (horizontální a vertikální osa)'
    );
  }

  protected transformation(board: GeoBoard, points: C[]): C[][] {
    const centerX = (board.width - 1) / 2;
    const centerY = (board.height - 1) / 2;

    // Horizontal mirror
    const mirroredH = points.map((p) => new C(Math.round(2 * centerX - p.x), p.y));
    // Vertical mirror
    const mirroredV = points.map((p) => new C(p.x, Math.round(2 * centerY - p.y)));
    // Both
    const mirroredBoth = points.map(
      (p) => new C(Math.round(2 * centerX - p.x), Math.round(2 * centerY - p.y))
    );

    return [mirroredH, mirroredV, mirroredBoth];
  }

  protected drawSpecific(page: PageGen): void {
    const centerX = (this.board.width - 1) / 2;
    const centerY = (this.board.height - 1) / 2;
    const axisPen = new PdfPen(PdfColor.fromRgb(128, 128, 128), 1);
    axisPen.dashArray = [5, 3];

    page.drawLine(
      axisPen,
      this.board.point(centerX, 0),
      this.board.point(centerX, this.board.height - 1)
    );
    page.drawLine(
      axisPen,
      this.board.point(0, centerY),
      this.board.point(this.board.width - 1, centerY)
    );
  }

  protected shapeSupported(board: GeoBoard, shape: GeoBoardShape): boolean {
    const centerX = (board.width - 1) / 2;
    const centerY = (board.height - 1) / 2;
    return shape.points.every((p) => p.x < centerX - 0.5 && p.y < centerY - 0.5);
  }

  protected get count(): number {
    return 1;
  }
}

export class GeoBoard__8__Soumernost_Osy2 extends GeoBoard_Soumernost_Osy2_Base {
  constructor() {
    super(GeoBoard.Board8());
  }
}

export class GeoBoard__920__Soumernost_Osy2 extends GeoBoard_Soumernost_Osy2_Base {
  constructor() {
    super(GeoBoard.Board920());
  }
}

// ===== Point symmetry (central symmetry) =====

class GeoBoard_Soumernost_Stredova_Base extends GeoBoardTransformationBase {
  protected description(data: GenData): string {
    return data.lang.GeoBoard_Soumernost_Stredova ?? 'Středová souměrnost';
  }

  protected transformation(board: GeoBoard, points: C[]): C[][] {
    const centerX = (board.width - 1) / 2;
    const centerY = (board.height - 1) / 2;

    const mirrored = points.map((p) => {
      return new C(Math.round(2 * centerX - p.x), Math.round(2 * centerY - p.y));
    });

    return [mirrored];
  }

  protected drawSpecific(page: PageGen): void {
    const centerX = (this.board.width - 1) / 2;
    const centerY = (this.board.height - 1) / 2;
    this.board.drawPoint(
      page,
      centerX,
      centerY,
      GUtil.basePen,
      PdfBrush.Black,
      PdfUnit.FromMillimeters(3)
    );
  }
}

export class GeoBoard__8__Soumernost_Stredova extends GeoBoard_Soumernost_Stredova_Base {
  constructor() {
    super(GeoBoard.Board8());
  }
}

export class GeoBoard__920__Soumernost_Stredova extends GeoBoard_Soumernost_Stredova_Base {
  constructor() {
    super(GeoBoard.Board920());
  }
}

// ===== Horizontal translation =====

class GeoBoard_PosunH_Base extends GeoBoardTransformationBase {
  protected description(data: GenData): string {
    return data.lang.GeoBoard_PosunH ?? 'Posun (horizontálně)';
  }

  protected transformation(board: GeoBoard, points: C[]): C[][] {
    const offset = (board.width - 1) / 2;
    const translated = points.map((p) => new C(offset + p.x, p.y));
    return [translated];
  }

  protected shapeSupported(board: GeoBoard, shape: GeoBoardShape): boolean {
    return shape.points.every((p) => p.x < board.width / 2);
  }

  protected drawHintLine(page: PageGen, pen: PdfPen, p1: C, p2: C): void {
    this.drawArrow(page, pen, this.board.pointFromC(p1), this.board.pointFromC(p2));
  }

  private drawArrow(page: PageGen, pen: PdfPen, start: PdfPoint, end: PdfPoint): void {
    page.drawLine(pen, start, end);
    const angle = Math.atan2(end.y - start.y, end.x - start.x);
    const arrowSize = this.board.distance / 2;
    const arrow1 = new PdfPoint(
      end.x - arrowSize * Math.cos(angle - Math.PI / 6),
      end.y - arrowSize * Math.sin(angle - Math.PI / 6)
    );
    const arrow2 = new PdfPoint(
      end.x - arrowSize * Math.cos(angle + Math.PI / 6),
      end.y - arrowSize * Math.sin(angle + Math.PI / 6)
    );
    page.drawLine(pen, end, arrow1);
    page.drawLine(pen, end, arrow2);
  }
}

export class GeoBoard__8__PosunH extends GeoBoard_PosunH_Base {
  constructor() {
    super(GeoBoard.Board8());
  }
}

export class GeoBoard__920__PosunH extends GeoBoard_PosunH_Base {
  constructor() {
    super(GeoBoard.Board920());
  }
}

// ===== Vertical translation =====

class GeoBoard_PosunV_Base extends GeoBoardTransformationBase {
  protected description(data: GenData): string {
    return data.lang.GeoBoard_PosunV ?? 'Posun (vertikálně)';
  }

  protected transformation(board: GeoBoard, points: C[]): C[][] {
    const offset = (board.height - 1) / 2;
    const translated = points.map((p) => new C(p.x, offset + p.y));
    return [translated];
  }

  protected shapeSupported(board: GeoBoard, shape: GeoBoardShape): boolean {
    return shape.points.every((p) => p.y < board.height / 2);
  }

  protected drawHintLine(page: PageGen, pen: PdfPen, p1: C, p2: C): void {
    this.drawArrow(page, pen, this.board.pointFromC(p1), this.board.pointFromC(p2));
  }

  private drawArrow(page: PageGen, pen: PdfPen, start: PdfPoint, end: PdfPoint): void {
    page.drawLine(pen, start, end);
    const angle = Math.atan2(end.y - start.y, end.x - start.x);
    const arrowSize = this.board.distance / 2;
    const arrow1 = new PdfPoint(
      end.x - arrowSize * Math.cos(angle - Math.PI / 6),
      end.y - arrowSize * Math.sin(angle - Math.PI / 6)
    );
    const arrow2 = new PdfPoint(
      end.x - arrowSize * Math.cos(angle + Math.PI / 6),
      end.y - arrowSize * Math.sin(angle + Math.PI / 6)
    );
    page.drawLine(pen, end, arrow1);
    page.drawLine(pen, end, arrow2);
  }
}

export class GeoBoard__8__PosunV extends GeoBoard_PosunV_Base {
  constructor() {
    super(GeoBoard.Board8());
  }
}

export class GeoBoard__920__PosunV extends GeoBoard_PosunV_Base {
  constructor() {
    super(GeoBoard.Board920());
  }
}

// ===== Scale (Enlargement) =====

class GeoBoard_Zvetseni_Base extends GeoBoardTransformationBase {
  protected description(data: GenData): string {
    return data.lang.GeoBoard_Zvetseni ?? 'Zvětšení';
  }

  protected transformation(board: GeoBoard, points: C[]): C[][] {
    const scaled = points.map((p) => new C(p.x * 2, p.y * 2));
    const maxX = Math.max(...scaled.map((p) => p.x));
    const maxY = Math.max(...scaled.map((p) => p.y));
    const dx = board.width - 1 - maxX;
    const dy = board.height - 1 - maxY;
    const translated = scaled.map((p) => new C(p.x + dx, p.y + dy));
    return [translated];
  }

  protected shapeSupported(board: GeoBoard, shape: GeoBoardShape): boolean {
    const hasX0 = shape.points.some((p) => p.x === 0);
    const hasY0 = shape.points.some((p) => p.y === 0);
    const withinHalf = shape.points.every((p) => p.x <= board.width / 2 && p.y <= board.height / 2);
    const sizeFits =
      shape.size.x <= (board.width - 1) / 2 && shape.size.y <= (board.height - 1) / 2;
    return withinHalf && hasX0 && hasY0 && sizeFits;
  }

  protected get count(): number {
    return 1;
  }

  protected drawHint(page: PageGen, shape: GeoBoardShape): void {}
}

export class GeoBoard__8__Zvetseni extends GeoBoard_Zvetseni_Base {
  constructor() {
    super(GeoBoard.Board8());
  }
}

export class GeoBoard__920__Zvetseni extends GeoBoard_Zvetseni_Base {
  constructor() {
    super(GeoBoard.Board920());
  }
}

// ===== Area calculation =====

// Format surface value as fraction string matching C# Fraction.Simplify().ToNumber()
// Surface values are always integers or half-integers (multiples of 0.5)
function surfaceToString(value: number): string {
  if (Number.isInteger(value)) return value.toString();
  const numer = Math.round(value * 2);
  const superDigits = ['⁰', '¹', '²', '³', '⁴', '⁵', '⁶', '⁷', '⁸', '⁹'];
  const sup = numer
    .toString()
    .split('')
    .map((d) => superDigits[+d])
    .join('');
  return `${sup}/₂`;
}

function getObsahShapeOptions(): Map<EGeoBoardShapeType, C[]> {
  const options = new Map<EGeoBoardShapeType, C[]>();
  options.set(EGeoBoardShapeType.Square, [new C(2, 2), new C(3, 3)]);
  options.set(EGeoBoardShapeType.Rect, [
    new C(2, 3),
    new C(3, 2),
    new C(2, 3),
    new C(3, 2),
    new C(4, 2),
    new C(2, 4),
    new C(4, 1),
    new C(1, 4),
  ]);
  options.set(EGeoBoardShapeType.Triangle, [
    new C(2, 2),
    new C(3, 3),
    new C(4, 4),
    new C(2, 3),
    new C(3, 2),
    new C(2, 3),
    new C(3, 2),
    new C(4, 2),
    new C(2, 4),
    new C(3, 4),
    new C(4, 3),
  ]);
  options.set(EGeoBoardShapeType.TetrisT, [new C(3, 2)]);
  return options;
}

function shapeCenter(shape: GeoBoardShape): C {
  const minX = Math.min(...shape.points.map((p) => p.x));
  const maxX = Math.max(...shape.points.map((p) => p.x));
  const minY = Math.min(...shape.points.map((p) => p.y));
  const maxY = Math.max(...shape.points.map((p) => p.y));
  return new C((minX + maxX) / 2, (minY + maxY) / 2);
}

class GeoBoard_Obsah_Base implements ILayoutBaseGen {
  constructor(protected readonly board: GeoBoard) {}

  public async generate(pdf: PdfGen, opts: OptsBase): Promise<void> {
    const page = pdf.addPage();
    const random = opts.data?.random ?? new Random();

    const hintText = opts.data.lang.GeoBoard_Obsah ?? 'Obsah geometrických tvarů';
    page.drawString(
      hintText,
      GUtil.baseFont,
      PdfBrush.Black,
      null,
      new PdfRect(PdfUnit.FromMillimeters(5), PdfUnit.FromMillimeters(5), 50, 10),
      ETextAlign.TopLeft
    );

    this.board.draw(page);

    const shapeOptions = getObsahShapeOptions();
    const shapes: GeoBoardShape[] = [];
    const origins = new Set<string>();
    let total = 0;

    const maxShapes = GUtil.geoBoardColors.length;
    let count = 0;

    while (count < maxShapes) {
      const candidates: GeoBoardShape[] = [];
      for (let i = 0; i < 5; i++) {
        candidates.push(
          randomGeoBoardShape(shapeOptions, this.board.width, this.board.height, () =>
            random.nextDouble()
          )
        );
      }

      let shape: GeoBoardShape;

      if (count === 0) {
        const valid = candidates.filter(
          (s) => s.origin.x === 0 && s.points.every((p) => p.x <= (this.board.width - 1) / 2)
        );
        if (valid.length === 0) continue;
        shape = valid[Math.floor(random.nextDouble() * valid.length)];
      } else {
        const scored = candidates
          .map((s) => {
            const center = shapeCenter(s);
            const distance = shapes
              .map((existing) => {
                const existingCenter = shapeCenter(existing);
                return (
                  Math.abs(center.x - existingCenter.x) + Math.abs(center.y - existingCenter.y)
                );
              })
              .reduce((a, b) => a + b, 0);
            return { shape: s, distance };
          })
          .sort((a, b) => b.distance - a.distance);
        shape = scored[0].shape;
      }

      const originKey = shape.origin.toHash();
      if (origins.has(originKey)) continue;

      total += shape.surface;
      origins.add(originKey);
      shapes.push(shape);
      count++;
    }

    for (let index = 0; index < shapes.length; index++) {
      const shape = shapes[index];
      const color = GUtil.geoBoardColors[index];
      const pen = new PdfPen(color, 2);
      const brush = new PdfBrush(
        PdfColor.fromRgb(
          Math.round(color.toRgbaArray()[0] * 255),
          Math.round(color.toRgbaArray()[1] * 255),
          Math.round(color.toRgbaArray()[2] * 255),
          0.2
        )
      );

      const points = shape.points.map((p) => this.board.pointFromC(p));
      const offsetPoints = points.map(
        (pt) =>
          new PdfPoint(
            pt.x + index * PdfUnit.FromMillimeters(0.2),
            pt.y + index * PdfUnit.FromMillimeters(0.2)
          )
      );
      page.drawPolygon(pen, brush, offsetPoints);
    }

    const columnCount = Math.ceil(shapes.length / 2);
    const rect = this.board.rectMini;
    const cellWidth = rect.width / columnCount;
    const cellHeight = rect.height / 2;

    const m1 = PdfUnit.FromMillimeters(2);
    const m2 = PdfUnit.FromMillimeters(2);
    const cellSize1 = new PdfSize(cellWidth - m1, cellHeight - m1);
    const cellSize2 = new PdfSize(cellSize1.width - 2 * m2, cellSize1.height - 2 * m2);

    const allRelPoints = shapes.flatMap((s) =>
      s.points.map((p) => new C(p.x - s.origin.x, p.y - s.origin.y))
    );
    const maxRelX = Math.max(...allRelPoints.map((p) => p.x));
    const maxRelY = Math.max(...allRelPoints.map((p) => p.y));
    const scale = Math.min((cellSize2.width / maxRelX) * 0.7, cellSize2.height / maxRelY);

    for (let x = 0; x < columnCount; x++) {
      for (let y = 0; y < 2; y++) {
        const rx = new PdfRect(
          rect.x + cellWidth * x,
          rect.y + cellHeight * y,
          cellWidth,
          cellHeight
        );
        const ro = new PdfRect(rx.x, rx.y, cellSize1.width, cellSize1.height);
        const r = new PdfRect(rx.x + m2, rx.y + m2, cellSize2.width, cellSize2.height);

        const index = x * 2 + y;
        if (index < shapes.length) {
          const color = GUtil.geoBoardColors[index];
          const pen = new PdfPen(color, 2);
          const brush = new PdfBrush(
            PdfColor.fromRgb(
              Math.round(color.toRgbaArray()[0] * 255),
              Math.round(color.toRgbaArray()[1] * 255),
              Math.round(color.toRgbaArray()[2] * 255),
              0.2
            )
          );

          page.drawRectangle(GUtil.basePen, null, ro);
          const shape = shapes[index];
          const relPoints = shape.points.map(
            (p) => new C(p.x - shape.origin.x, p.y - shape.origin.y)
          );
          const miniPoints = relPoints.map(
            (p) => new PdfPoint(r.x + p.x * scale, r.y + p.y * scale)
          );
          page.drawPolygon(pen, brush, miniPoints);

          const areaText = `S = ${surfaceToString(shape.surface)}`;
          page.drawString(
            areaText,
            GUtil.baseFont,
            PdfBrush.Black,
            null,
            r,
            ETextAlign.BottomRight
          );
        } else if (index === shapes.length) {
          const label = opts.data.lang.GeoBoard_TotalArea ?? 'Obsah celkem';
          const totalText = `S = ${surfaceToString(total)}`;
          page.drawStrings(
            [label, totalText],
            GUtil.baseFont,
            PdfBrush.Black,
            null,
            r,
            ETextAlign.Center
          );
        }
      }
    }
  }
}

export class GeoBoard__8__Obsah extends GeoBoard_Obsah_Base {
  constructor() {
    super(GeoBoard.Board8());
  }
}

export class GeoBoard__920__Obsah extends GeoBoard_Obsah_Base {
  constructor() {
    super(GeoBoard.Board920());
  }
}

// ===== Division =====

function sideOfLine(p1: C, p2: C, a: C): number {
  return (p2.x - p1.x) * (a.y - p1.y) - (p2.y - p1.y) * (a.x - p1.x);
}

function distanceFromLine(p1: C, p2: C, a: C): number {
  const numerator = Math.abs((p2.x - p1.x) * (p1.y - a.y) - (p1.x - a.x) * (p2.y - p1.y));
  const denominator = Math.sqrt((p2.x - p1.x)**2 + (p2.y - p1.y)**2);
  return numerator / denominator;
}

function sideKey(points: C[], point: C): string {
  let sb = '';
  for (let i = 0; i < points.length; i += 2) {
    const side = sideOfLine(points[i], points[i + 1], point);
    const distance = distanceFromLine(points[i], points[i + 1], point);
    if (side === 0 || distance < 0.4) {
      sb += '0';
    } else if (side > 0) {
      sb += '+';
    } else {
      sb += '-';
    }
  }
  return sb;
}

class GeoBoard_Rozdeleni_Base implements ILayoutBaseGen {
  constructor(protected readonly board: GeoBoard) {}

  public async generate(pdf: PdfGen, opts: OptsBase): Promise<void> {
    const page = pdf.addPage();
    const random = opts.data?.random ?? new Random();

    const hintText =
      opts.data.lang.GeoBoard_Rozdeleni ?? 'Rozdělní dvěma čárami na stejný počet bodů';
    page.drawString(
      hintText,
      GUtil.baseFont,
      PdfBrush.Black,
      null,
      new PdfRect(PdfUnit.FromMillimeters(5), PdfUnit.FromMillimeters(5), 50, 10),
      ETextAlign.TopLeft
    );

    const stones = 5;
    const divisions = 2;

    const all: C[] = [];
    for (let x = 0; x < this.board.width; x++) {
      for (let y = 0; y < this.board.height; y++) {
        all.push(new C(x, y));
      }
    }

    const borders = all.filter(
      (p) => p.x === 0 || p.y === 0 || p.x + 1 === this.board.width || p.y + 1 === this.board.height
    );

    let counter = 0;
    let borderPoints: C[] = [];
    let parts = new Map<string, C[]>();

    while (true) {
      const shuffled = borders.slice().sort(() => random.nextDouble() - 0.5);
      borderPoints = shuffled.slice(0, divisions * 2);

      const inf = new Map<string, C[]>();
      for (const point of all) {
        const key = sideKey(borderPoints, point);
        if (!key.includes('0')) {
          if (!inf.has(key)) {
            inf.set(key, []);
          }
          inf.get(key)!.push(point);
        }
      }

      if (inf.size > 3 && Array.from(inf.values()).every((v) => v.length > stones + 2)) {
        parts = inf;
        break;
      }

      if (counter++ > 10000) throw new Error('Timeout generating Rozdeleni');
    }

    this.board.draw(page);
    this.board.drawMini(page);

    const pointSize = PdfUnit.FromMillimeters(1.5);
    const stoneSize = PdfUnit.FromMillimeters(6);

    for (const [, points] of parts) {
      const shuffled = points.sort(() => random.nextDouble() - 0.5);
      const selected = shuffled.slice(0, stones);

      for (const point of selected) {
        const p1 = this.board.pointFromC(point);
        const e1 = new PdfRect(p1.x - stoneSize / 2, p1.y - stoneSize / 2, stoneSize, stoneSize);
        page.drawEllipse(null, PdfBrush.Black, e1);

        const p2 = this.board.pointMiniFromC(point);
        const e2 = new PdfRect(p2.x - pointSize / 2, p2.y - pointSize / 2, pointSize, pointSize);
        page.drawEllipse(null, PdfBrush.Black, e2);
      }
    }

    for (let i = 0; i < borderPoints.length; i += 2) {
      const p1 = this.board.pointMiniFromC(borderPoints[i]);
      const p2 = this.board.pointMiniFromC(borderPoints[i + 1]);
      page.drawLine(GUtil.basePen, p1, p2);
    }
  }
}

export class GeoBoard__8__Rozdeleni extends GeoBoard_Rozdeleni_Base {
  constructor() {
    super(GeoBoard.Board8());
  }
}

export class GeoBoard__920__Rozdeleni extends GeoBoard_Rozdeleni_Base {
  constructor() {
    super(GeoBoard.Board920());
  }
}
